/**
 * useOperationalChecklists.js
 *
 * Domain hook for operational checklists and permits - Anchor Mode v2.0 compliant
 * Centralizes all checklist-related storage and business logic
 *
 * Handles:
 * - SMS checklists (Hot Work, Enclosed Space, Lifting)
 * - Operational checklists (Bunkering, Anchoring, Mooring)
 * - Company custom checklists
 * - Permit management with validity tracking
 *
 * Base keys:
 * - 'operations_checklists' (completed checklists)
 * - 'operations_permits' (active/completed permits)
 * - 'operations_checklist_templates' (custom templates)
 * - 'operations_bunkering' (bunkering records)
 * - 'operations_checklist_favourites' (user favourites)
 *
 * @example
 * const {
 *   getChecklistTemplates,
 *   startChecklist,
 *   issuePermit,
 *   getActivePermits
 * } = useOperationalChecklists();
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
import {
  CHECKLIST_CATEGORIES,
  PERMIT_STATUS,
  PERMIT_TYPES,
  BUNKERING_TYPES,
  OPERATIONS_STORAGE_KEYS,
  PERMIT_TIME_THRESHOLDS,
  DEFAULT_PERMIT_VALIDITY_HOURS
} from '../../../config/operationsConfig';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Generate unique ID
 */
const generateId = (prefix) => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Default SMS checklist templates (WBC3 required)
 */
const SMS_CHECKLIST_TEMPLATES = {
  hot_work: {
    id: 'hot_work',
    name: 'Hot Work Permit',
    category: CHECKLIST_CATEGORIES.SMS,
    icon: 'Flame',
    description: 'Required for welding, grinding, cutting, or any work producing heat, sparks, or flame.',
    reference: 'WBC3 Section 28.2',
    isPermit: true,
    defaultValidityHours: 4,
    fields: {
      workDescription: { type: 'textarea', label: 'Work Description', required: true, voice: true },
      location: { type: 'text', label: 'Location', required: true },
      responsiblePerson: { type: 'crew', label: 'Responsible Person (performing work)', required: true },
      authorisedBy: { type: 'crew', label: 'Authorised By (Master or delegated authority)', required: true },
      fireWatch: { type: 'crew', label: 'Fire Watch', required: true }
    },
    items: [
      { id: 'area_inspected', text: 'Area inspected for flammable materials', required: true },
      { id: 'extinguisher_positioned', text: 'Fire extinguisher positioned within 2m of work area', required: true },
      { id: 'combustibles_removed', text: 'Combustibles removed or protected', required: true },
      { id: 'ventilation_adequate', text: 'Ventilation adequate', required: true },
      { id: 'ppe_available', text: 'Appropriate PPE available', required: true },
      { id: 'fire_watch_assigned', text: 'Fire watch assigned', required: true },
      { id: 'crew_notified', text: 'Other crew notified', required: true }
    ]
  },
  enclosed_space: {
    id: 'enclosed_space',
    name: 'Enclosed Space Entry',
    category: CHECKLIST_CATEGORIES.SMS,
    icon: 'DoorOpen',
    description: 'Required for entry into enclosed or confined spaces.',
    reference: 'WBC3 Section 28.3',
    isPermit: true,
    defaultValidityHours: 4,
    fields: {
      spaceDescription: { type: 'text', label: 'Space Description', required: true },
      location: { type: 'text', label: 'Location', required: true },
      responsiblePerson: { type: 'crew', label: 'Responsible Person', required: true },
      authorisedBy: { type: 'crew', label: 'Authorised By', required: true },
      standbyPerson: { type: 'crew', label: 'Standby Person', required: true }
    },
    items: [
      { id: 'space_ventilated', text: 'Space adequately ventilated', required: true },
      { id: 'atmosphere_tested', text: 'Atmosphere tested and safe', required: true },
      { id: 'rescue_equipment', text: 'Rescue equipment available', required: true },
      { id: 'communication_established', text: 'Communication established with standby', required: true },
      { id: 'lighting_adequate', text: 'Lighting adequate', required: true },
      { id: 'entry_logged', text: 'Entry logged and time recorded', required: true }
    ]
  },
  lifting: {
    id: 'lifting',
    name: 'Lifting Operations',
    category: CHECKLIST_CATEGORIES.SMS,
    icon: 'ArrowUpFromLine',
    description: 'Required for crane and lifting gear operations.',
    reference: 'WBC3 Section 25.2 / LOLER',
    isPermit: true,
    defaultValidityHours: 8,
    fields: {
      liftDescription: { type: 'textarea', label: 'Lift Description', required: true, voice: true },
      loadWeight: { type: 'number', label: 'Load Weight (kg)', required: true },
      equipmentUsed: { type: 'text', label: 'Equipment Used', required: true },
      responsiblePerson: { type: 'crew', label: 'Responsible Person', required: true },
      authorisedBy: { type: 'crew', label: 'Authorised By', required: true }
    },
    items: [
      { id: 'equipment_inspected', text: 'Lifting equipment inspected before use', required: true },
      { id: 'swl_not_exceeded', text: 'SWL not exceeded', required: true },
      { id: 'lift_area_clear', text: 'Lift area clear of personnel', required: true },
      { id: 'load_secured', text: 'Load properly secured and balanced', required: true },
      { id: 'communications_confirmed', text: 'Communications confirmed between all parties', required: true },
      { id: 'weather_checked', text: 'Weather conditions suitable', required: true }
    ]
  }
};

/**
 * Default operational checklist templates
 */
const OPERATIONAL_CHECKLIST_TEMPLATES = {
  bunkering_full: {
    id: 'bunkering_full',
    name: 'Bunkering Operations',
    category: CHECKLIST_CATEGORIES.OPERATIONAL,
    icon: 'Fuel',
    description: 'Full bunkering checklist for ship-to-ship or barge delivery.',
    reference: 'MARPOL Annex I',
    isPermit: false,
    pages: 3,
    fields: {
      responsiblePerson: { type: 'crew', label: 'Responsible Person', required: true },
      fuelType: { type: 'select', label: 'Fuel Type', required: true, options: ['Diesel (MGO)', 'Petrol'] },
      plannedQuantity: { type: 'number', label: 'Planned Quantity (litres)', required: true },
      supplier: { type: 'text', label: 'Supplier', required: true },
      receivedQuantity: { type: 'number', label: 'Final Quantity Received (litres)', required: true },
      bunkerReceiptRetained: { type: 'checkbox', label: 'Bunker receipt copy retained on board', required: true },
      spillageOccurred: { type: 'boolean', label: 'Any spillage?', required: true },
      spillageDetails: { type: 'textarea', label: 'Spillage Details', required: false, conditional: 'spillageOccurred' }
    },
    itemGroups: {
      preBunkering: {
        title: 'Pre-Bunkering Checks',
        items: [
          { id: 'tank_capacity', text: 'Tank capacities confirmed - no overflow risk', required: true },
          { id: 'crew_aware', text: 'All crew aware bunkering in progress', required: true },
          { id: 'no_smoking', text: 'No smoking signs displayed', required: true },
          { id: 'extinguisher_ready', text: 'Fire extinguisher ready at manifold', required: true },
          { id: 'scuppers_plugged', text: 'Scuppers plugged', required: true },
          { id: 'absorbent_ready', text: 'Absorbent materials ready', required: true },
          { id: 'comms_established', text: 'Communications established with supplier', required: true }
        ]
      },
      duringBunkering: {
        title: 'During Bunkering',
        items: [
          { id: 'pump_rate_checked', text: 'Pump rate checked - not exceeding safe limit', required: true },
          { id: 'tank_levels_monitored', text: 'Tank levels monitored', required: true },
          { id: 'no_leaks', text: 'No leaks observed', required: true },
          { id: 'topping_off', text: 'Topping off procedure followed', required: true }
        ]
      },
      postBunkering: {
        title: 'Post-Bunkering',
        items: [
          { id: 'transfer_completed', text: 'Transfer completed safely', required: true },
          { id: 'hoses_disconnected', text: 'Hoses disconnected and secured', required: true },
          { id: 'quantities_recorded', text: 'Final quantities recorded', required: true },
          { id: 'scuppers_unplugged', text: 'Scuppers unplugged', required: true },
          { id: 'fire_equipment_returned', text: 'Fire equipment returned to normal positions', required: true }
        ]
      }
    }
  },
  anchoring: {
    id: 'anchoring',
    name: 'Anchoring',
    category: CHECKLIST_CATEGORIES.OPERATIONAL,
    icon: 'Anchor',
    description: 'Checklist for anchoring operations.',
    reference: 'WBC3 Section 26',
    isPermit: false,
    fields: {
      responsiblePerson: { type: 'crew', label: 'Responsible Person', required: true },
      location: { type: 'text', label: 'Anchoring Location', required: true },
      depth: { type: 'number', label: 'Depth (metres)', required: false },
      chainDeployed: { type: 'number', label: 'Chain/Rope Deployed (metres)', required: false }
    },
    items: [
      { id: 'position_suitable', text: 'Anchoring position suitable and clear of hazards', required: true },
      { id: 'depth_checked', text: 'Depth checked and appropriate', required: true },
      { id: 'windlass_tested', text: 'Windlass tested before arrival', required: true },
      { id: 'anchor_party_briefed', text: 'Anchor party briefed', required: true },
      { id: 'comms_established', text: 'Communications established bridge/foredeck', required: true },
      { id: 'anchor_lights_ready', text: 'Anchor lights/shapes ready', required: false }
    ]
  },
  mooring: {
    id: 'mooring',
    name: 'Pre-Arrival/Mooring',
    category: CHECKLIST_CATEGORIES.OPERATIONAL,
    icon: 'Ship',
    description: 'Pre-arrival and mooring checklist.',
    reference: 'WBC3 Section 26',
    isPermit: false,
    fields: {
      responsiblePerson: { type: 'crew', label: 'Responsible Person', required: true },
      berth: { type: 'text', label: 'Berth/Location', required: true }
    },
    items: [
      { id: 'mooring_plan', text: 'Mooring plan reviewed', required: true },
      { id: 'lines_prepared', text: 'Mooring lines prepared and ready', required: true },
      { id: 'fenders_positioned', text: 'Fenders positioned', required: true },
      { id: 'deck_party_briefed', text: 'Deck party briefed', required: true },
      { id: 'approach_clear', text: 'Approach to berth clear', required: true }
    ]
  },
  diving_support: {
    id: 'diving_support',
    name: 'Diving Support',
    category: CHECKLIST_CATEGORIES.OPERATIONAL,
    icon: 'Waves',
    description: 'Vessel support checklist for diving operations. Note: Diving operations are regulated under HSE Diving at Work Regulations 1997, not WBC3.',
    reference: 'HSE Diving at Work Regulations 1997',
    isPermit: false,
    isOperational: true, // Show "Operational" badge
    fields: {
      diveContractorCompany: { type: 'text', label: 'Dive Contractor Company', required: true },
      diveSupervisor: { type: 'text', label: 'Dive Supervisor', required: true },
      diveStarted: { type: 'time', label: 'Dive Started', required: false },
      diveEnded: { type: 'time', label: 'Dive Ended', required: false },
      notes: { type: 'textarea', label: 'Notes', required: false, voice: true }
    },
    items: [
      { id: 'vessel_positioned', text: 'Vessel positioned as requested by dive contractor', required: true },
      { id: 'diver_lift_ready', text: 'Diver lift/recovery equipment ready (if fitted)', required: false },
      { id: 'first_aid_ready', text: 'First aid and O2 equipment ready', required: true },
      { id: 'comms_established', text: 'Communications established with dive team', required: true },
      { id: 'rotating_secured', text: 'All rotating equipment secured (props, thrusters)', required: true },
      { id: 'crew_briefed', text: 'Crew briefed on support duties', required: true }
    ]
  }
};

export const useOperationalChecklists = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Storage
  const { value: checklists = [], setValue: setChecklists } = useStorageState(
    OPERATIONS_STORAGE_KEYS.CHECKLISTS,
    []
  );

  const { value: permits = [], setValue: setPermits } = useStorageState(
    OPERATIONS_STORAGE_KEYS.PERMITS,
    []
  );

  const { value: customTemplates = [], setValue: setCustomTemplates } = useStorageState(
    OPERATIONS_STORAGE_KEYS.CHECKLIST_TEMPLATES,
    []
  );

  const { value: bunkeringRecords = [], setValue: setBunkeringRecords } = useStorageState(
    OPERATIONS_STORAGE_KEYS.BUNKERING_RECORDS,
    []
  );

  const { value: favourites = [], setValue: setFavourites } = useStorageState(
    OPERATIONS_STORAGE_KEYS.FAVOURITES,
    []
  );

  // ============================================================================
  // TEMPLATE MANAGEMENT
  // ============================================================================

  /**
   * Get all available checklist templates
   */
  const getChecklistTemplates = useCallback((category = null) => {
    const allTemplates = {
      ...SMS_CHECKLIST_TEMPLATES,
      ...OPERATIONAL_CHECKLIST_TEMPLATES,
      ...customTemplates.reduce((acc, t) => ({ ...acc, [t.id]: t }), {})
    };

    if (!category) {
      return allTemplates;
    }

    return Object.fromEntries(
      Object.entries(allTemplates).filter(([, template]) => template.category === category)
    );
  }, [customTemplates]);

  /**
   * Get a specific template by ID
   */
  const getTemplate = useCallback((templateId) => {
    return (
      SMS_CHECKLIST_TEMPLATES[templateId] ||
      OPERATIONAL_CHECKLIST_TEMPLATES[templateId] ||
      customTemplates.find(t => t.id === templateId) ||
      null
    );
  }, [customTemplates]);

  /**
   * Get favourite checklists
   */
  const getFavourites = useCallback(() => {
    return favourites.slice(0, 5).map(id => getTemplate(id)).filter(Boolean);
  }, [favourites, getTemplate]);

  /**
   * Toggle favourite status
   */
  const toggleFavourite = useCallback(async (templateId) => {
    const isFav = favourites.includes(templateId);
    let updated;

    if (isFav) {
      updated = favourites.filter(id => id !== templateId);
    } else {
      if (favourites.length >= 5) {
        // Remove oldest favourite
        updated = [...favourites.slice(1), templateId];
      } else {
        updated = [...favourites, templateId];
      }
    }

    await setFavourites(updated);
  }, [favourites, setFavourites]);

  // ============================================================================
  // CHECKLIST OPERATIONS
  // ============================================================================

  /**
   * Start a new checklist
   */
  const startChecklist = useCallback(async (templateId, initialData = {}) => {
    if (!canDo(can, 'checklists', 'create')) {
      console.warn('[useOperationalChecklists] Permission denied: cannot create checklist');
      return null;
    }

    const template = getTemplate(templateId);
    if (!template) {
      console.warn('[useOperationalChecklists] Template not found:', templateId);
      return null;
    }

    const now = new Date().toISOString();

    // Build items from template
    let items = [];
    if (template.items) {
      items = template.items.map(item => ({
        ...item,
        completed: false,
        completedAt: null,
        completedBy: null
      }));
    } else if (template.itemGroups) {
      // Multi-page checklist
      Object.values(template.itemGroups).forEach(group => {
        group.items.forEach(item => {
          items.push({
            ...item,
            group: group.title,
            completed: false,
            completedAt: null,
            completedBy: null
          });
        });
      });
    }

    const newChecklist = {
      id: generateId('checklist'),
      templateId,
      templateName: template.name,
      category: template.category,
      status: 'in_progress',
      items,
      fields: initialData,
      startedAt: now,
      startedBy: currentUser?.name || 'Unknown',
      completedAt: null,
      completedBy: null
    };

    const updated = [...checklists, newChecklist];
    await setChecklists(updated);

    return newChecklist;
  }, [can, getTemplate, checklists, setChecklists, currentUser]);

  /**
   * Update a checklist item
   */
  const updateChecklistItem = useCallback(async (checklistId, itemId, completed) => {
    if (!canDo(can, 'checklists', 'update')) {
      console.warn('[useOperationalChecklists] Permission denied: cannot update checklist');
      return false;
    }

    const checklist = checklists.find(c => c.id === checklistId);
    if (!checklist || checklist.status !== 'in_progress') {
      return false;
    }

    const now = new Date().toISOString();

    const updatedItems = checklist.items.map(item =>
      item.id === itemId
        ? {
            ...item,
            completed,
            completedAt: completed ? now : null,
            completedBy: completed ? currentUser?.name || 'Unknown' : null
          }
        : item
    );

    const updated = checklists.map(c =>
      c.id === checklistId
        ? { ...c, items: updatedItems, updatedAt: now }
        : c
    );

    await setChecklists(updated);
    return true;
  }, [can, checklists, setChecklists, currentUser]);

  /**
   * Update checklist field data
   */
  const updateChecklistFields = useCallback(async (checklistId, fields) => {
    if (!canDo(can, 'checklists', 'update')) {
      return false;
    }

    const updated = checklists.map(c =>
      c.id === checklistId
        ? { ...c, fields: { ...c.fields, ...fields }, updatedAt: new Date().toISOString() }
        : c
    );

    await setChecklists(updated);
    return true;
  }, [can, checklists, setChecklists]);

  /**
   * Complete a checklist
   */
  const completeChecklist = useCallback(async (checklistId, finalData = {}) => {
    if (!canDo(can, 'checklists', 'update')) {
      return false;
    }

    const checklist = checklists.find(c => c.id === checklistId);
    if (!checklist || checklist.status !== 'in_progress') {
      return false;
    }

    // Check if all required items are complete
    const requiredIncomplete = checklist.items.filter(i => i.required && !i.completed);
    if (requiredIncomplete.length > 0) {
      console.warn('[useOperationalChecklists] Cannot complete: required items incomplete');
      return { success: false, reason: 'incomplete', items: requiredIncomplete };
    }

    const now = new Date().toISOString();

    const updated = checklists.map(c =>
      c.id === checklistId
        ? {
            ...c,
            status: 'completed',
            fields: { ...c.fields, ...finalData },
            completedAt: now,
            completedBy: currentUser?.name || 'Unknown'
          }
        : c
    );

    await setChecklists(updated);
    return { success: true };
  }, [can, checklists, setChecklists, currentUser]);

  /**
   * Get active (in-progress) checklists
   */
  const getActiveChecklists = useCallback(() => {
    return checklists.filter(c => c.status === 'in_progress');
  }, [checklists]);

  /**
   * Get checklists completed today
   */
  const getCompletedToday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return checklists.filter(c => {
      if (c.status !== 'completed' || !c.completedAt) return false;
      const completedDate = new Date(c.completedAt);
      completedDate.setHours(0, 0, 0, 0);
      return completedDate.getTime() === today.getTime();
    });
  }, [checklists]);

  // ============================================================================
  // PERMIT OPERATIONS
  // ============================================================================

  /**
   * Issue a new permit
   */
  const issuePermit = useCallback(async (templateId, permitData) => {
    if (!canDo(can, 'permits', 'create')) {
      console.warn('[useOperationalChecklists] Permission denied: cannot create permit');
      return null;
    }

    const template = getTemplate(templateId);
    if (!template || !template.isPermit) {
      console.warn('[useOperationalChecklists] Template not found or not a permit:', templateId);
      return null;
    }

    const now = new Date();
    const validFrom = permitData.validFrom ? new Date(permitData.validFrom) : now;
    const validityHours = permitData.validityHours || template.defaultValidityHours || DEFAULT_PERMIT_VALIDITY_HOURS;
    const validTo = permitData.validTo
      ? new Date(permitData.validTo)
      : new Date(validFrom.getTime() + validityHours * 60 * 60 * 1000);

    // Build items from template
    const items = template.items.map(item => ({
      ...item,
      completed: permitData.items?.[item.id] || false,
      completedAt: permitData.items?.[item.id] ? now.toISOString() : null,
      completedBy: permitData.items?.[item.id] ? currentUser?.name || 'Unknown' : null
    }));

    // Check if all required items are complete before issuing
    const requiredIncomplete = items.filter(i => i.required && !i.completed);
    if (requiredIncomplete.length > 0) {
      console.warn('[useOperationalChecklists] Cannot issue permit: required items incomplete');
      return { success: false, reason: 'incomplete', items: requiredIncomplete };
    }

    const newPermit = {
      id: generateId('permit'),
      templateId,
      templateName: template.name,
      permitType: templateId,
      status: PERMIT_STATUS.ISSUED,
      items,
      fields: permitData.fields || {},
      validFrom: validFrom.toISOString(),
      validTo: validTo.toISOString(),
      issuedAt: now.toISOString(),
      issuedBy: currentUser?.name || 'Unknown',
      extensions: [],
      completedAt: null,
      completedBy: null,
      cancelledAt: null,
      cancelledBy: null,
      cancelReason: null
    };

    const updated = [...permits, newPermit];
    await setPermits(updated);

    return { success: true, permit: newPermit };
  }, [can, getTemplate, permits, setPermits, currentUser]);

  /**
   * Extend permit validity
   */
  const extendPermit = useCallback(async (permitId, newEndTime, reason) => {
    if (!canDo(can, 'permits', 'update')) {
      return false;
    }

    if (!reason || reason.trim() === '') {
      console.warn('[useOperationalChecklists] Reason required for permit extension');
      return false;
    }

    const permit = permits.find(p => p.id === permitId);
    if (!permit || permit.status !== PERMIT_STATUS.ISSUED) {
      return false;
    }

    const now = new Date().toISOString();

    const extension = {
      extendedAt: now,
      extendedBy: currentUser?.name || 'Unknown',
      previousValidTo: permit.validTo,
      newValidTo: newEndTime,
      reason
    };

    const updated = permits.map(p =>
      p.id === permitId
        ? {
            ...p,
            status: PERMIT_STATUS.EXTENDED,
            validTo: newEndTime,
            extensions: [...(p.extensions || []), extension],
            updatedAt: now
          }
        : p
    );

    await setPermits(updated);
    return true;
  }, [can, permits, setPermits, currentUser]);

  /**
   * Complete a permit normally
   */
  const completePermit = useCallback(async (permitId, notes = '') => {
    if (!canDo(can, 'permits', 'update')) {
      return false;
    }

    const permit = permits.find(p => p.id === permitId);
    if (!permit || (permit.status !== PERMIT_STATUS.ISSUED && permit.status !== PERMIT_STATUS.EXTENDED)) {
      return false;
    }

    const now = new Date().toISOString();

    const updated = permits.map(p =>
      p.id === permitId
        ? {
            ...p,
            status: PERMIT_STATUS.COMPLETED,
            completedAt: now,
            completedBy: currentUser?.name || 'Unknown',
            completionNotes: notes
          }
        : p
    );

    await setPermits(updated);
    return true;
  }, [can, permits, setPermits, currentUser]);

  /**
   * Cancel a permit
   */
  const cancelPermit = useCallback(async (permitId, reason) => {
    if (!canDo(can, 'permits', 'update')) {
      return false;
    }

    if (!reason || reason.trim() === '') {
      console.warn('[useOperationalChecklists] Reason required for permit cancellation');
      return false;
    }

    const permit = permits.find(p => p.id === permitId);
    if (!permit || (permit.status !== PERMIT_STATUS.ISSUED && permit.status !== PERMIT_STATUS.EXTENDED)) {
      return false;
    }

    const now = new Date().toISOString();

    const updated = permits.map(p =>
      p.id === permitId
        ? {
            ...p,
            status: PERMIT_STATUS.CANCELLED,
            cancelledAt: now,
            cancelledBy: currentUser?.name || 'Unknown',
            cancelReason: reason
          }
        : p
    );

    await setPermits(updated);
    return true;
  }, [can, permits, setPermits, currentUser]);

  /**
   * Get active permits
   */
  const getActivePermits = useCallback(() => {
    const now = new Date();

    return permits
      .filter(p => p.status === PERMIT_STATUS.ISSUED || p.status === PERMIT_STATUS.EXTENDED)
      .map(p => {
        const validTo = new Date(p.validTo);
        const timeRemaining = validTo - now;

        let warningLevel = 'safe'; // green
        if (timeRemaining <= PERMIT_TIME_THRESHOLDS.CRITICAL) {
          warningLevel = 'critical'; // red
        } else if (timeRemaining <= PERMIT_TIME_THRESHOLDS.WARNING) {
          warningLevel = 'warning'; // amber
        }

        return {
          ...p,
          timeRemaining,
          warningLevel,
          isExpired: timeRemaining <= 0
        };
      })
      .sort((a, b) => a.timeRemaining - b.timeRemaining);
  }, [permits]);

  /**
   * Check for and flag expired permits
   */
  const checkExpiredPermits = useCallback(async () => {
    const now = new Date();
    let hasExpired = false;

    const updated = permits.map(p => {
      if (
        (p.status === PERMIT_STATUS.ISSUED || p.status === PERMIT_STATUS.EXTENDED) &&
        new Date(p.validTo) <= now
      ) {
        hasExpired = true;
        return {
          ...p,
          status: PERMIT_STATUS.EXPIRED,
          expiredAt: now.toISOString()
        };
      }
      return p;
    });

    if (hasExpired) {
      await setPermits(updated);
    }

    return hasExpired;
  }, [permits, setPermits]);

  // Auto-check for expired permits
  useEffect(() => {
    const interval = setInterval(() => {
      checkExpiredPermits();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [checkExpiredPermits]);

  // ============================================================================
  // BUNKERING OPERATIONS
  // ============================================================================

  /**
   * Log simple fuel top-up
   */
  const logSimpleBunkering = useCallback(async (data) => {
    if (!canDo(can, 'bunkering', 'create')) {
      return null;
    }

    const record = {
      id: generateId('bunkering'),
      type: BUNKERING_TYPES.SIMPLE,
      dateTime: data.dateTime || new Date().toISOString(),
      fuelType: data.fuelType,
      quantity: data.quantity,
      filledBy: data.filledBy || currentUser?.name || 'Unknown',
      notes: data.notes || '',
      createdAt: new Date().toISOString()
    };

    const updated = [...bunkeringRecords, record];
    await setBunkeringRecords(updated);

    return record;
  }, [can, bunkeringRecords, setBunkeringRecords, currentUser]);

  /**
   * Get bunkering records
   */
  const getBunkeringRecords = useCallback((limit = 10) => {
    return bunkeringRecords
      .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
      .slice(0, limit);
  }, [bunkeringRecords]);

  // ============================================================================
  // CUSTOM TEMPLATE MANAGEMENT
  // ============================================================================

  /**
   * Create a custom checklist template (admin only)
   */
  const createCustomTemplate = useCallback(async (templateData) => {
    if (!canDo(can, 'templates', 'create')) {
      console.warn('[useOperationalChecklists] Permission denied: cannot create template');
      return null;
    }

    const newTemplate = {
      id: generateId('template'),
      ...templateData,
      category: templateData.category || CHECKLIST_CATEGORIES.COMPANY,
      createdAt: new Date().toISOString(),
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      version: 1
    };

    const updated = [...customTemplates, newTemplate];
    await setCustomTemplates(updated);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.CREATE,
        'checklist_template',
        { id: newTemplate.id, vesselId: currentVessel?.id, name: newTemplate.name },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' }
      );
    } catch (auditError) {
      console.warn('[useOperationalChecklists] Audit trail logging failed:', auditError);
    }

    return newTemplate;
  }, [can, customTemplates, setCustomTemplates, currentUser, currentVessel]);

  /**
   * Update a custom template
   */
  const updateCustomTemplate = useCallback(async (templateId, updates) => {
    if (!canDo(can, 'templates', 'update')) {
      return false;
    }

    const template = customTemplates.find(t => t.id === templateId);
    if (!template) return false;

    const updated = customTemplates.map(t =>
      t.id === templateId
        ? {
            ...t,
            ...updates,
            updatedAt: new Date().toISOString(),
            version: (t.version || 1) + 1
          }
        : t
    );

    await setCustomTemplates(updated);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.UPDATE,
        'checklist_template',
        { id: templateId, vesselId: currentVessel?.id, changes: Object.keys(updates) },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' },
        template  // beforeState
      );
    } catch (auditError) {
      console.warn('[useOperationalChecklists] Audit trail logging failed:', auditError);
    }

    return true;
  }, [can, customTemplates, setCustomTemplates, currentVessel, currentUser]);

  /**
   * Delete a custom template
   */
  const deleteCustomTemplate = useCallback(async (templateId) => {
    if (!canDo(can, 'templates', 'delete')) {
      return false;
    }

    const template = customTemplates.find(t => t.id === templateId);
    const updated = customTemplates.filter(t => t.id !== templateId);
    await setCustomTemplates(updated);

    // Log to audit trail
    try {
      await logCRUDOperation(
        // collections, // Phase 2.6: Disabled for IndexedDB migration
        AUDIT_ACTIONS.DELETE,
        'checklist_template',
        { id: templateId, vesselId: currentVessel?.id },
        { userId: currentUser?.id, name: currentUser?.name || 'Unknown' },
        template  // beforeState
      );
    } catch (auditError) {
      console.warn('[useOperationalChecklists] Audit trail logging failed:', auditError);
    }

    return true;
  }, [can, customTemplates, setCustomTemplates, currentVessel, currentUser]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Templates
    getChecklistTemplates,
    getTemplate,
    getFavourites,
    toggleFavourite,

    // Checklists
    startChecklist,
    updateChecklistItem,
    updateChecklistFields,
    completeChecklist,
    getActiveChecklists,
    getCompletedToday,
    checklists,

    // Permits
    issuePermit,
    extendPermit,
    completePermit,
    cancelPermit,
    getActivePermits,
    checkExpiredPermits,
    permits,

    // Bunkering
    logSimpleBunkering,
    getBunkeringRecords,
    bunkeringRecords,

    // Custom templates
    createCustomTemplate,
    updateCustomTemplate,
    deleteCustomTemplate,
    customTemplates,

    // Constants
    CHECKLIST_CATEGORIES,
    PERMIT_STATUS,
    PERMIT_TYPES,
    BUNKERING_TYPES
  };
};

export default useOperationalChecklists;
